import React, { useState, useEffect } from 'react';
import ModernLayout from './ModernLayout';
import BoardDeckLeftPanel from './BoardDeckLeftPanel';
import BoardDeckCenterPanel from './BoardDeckCenterPanel';
import LoadingOverlay from './LoadingOverlay';
import './ModernBoardDeckGenerator.css';
import config, { getDefaultHeaders } from '../config/api';
import { useCountry } from '../context/CountryContext';

const ModernBoardDeckGenerator = () => {
  const { country } = useCountry();
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(null);

  // Board deck specific state
  const [mode, setMode] = useState('auto');
  const [tone, setTone] = useState('formal');
  const [targetAudience, setTargetAudience] = useState('analysts');
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [targetWordCount, setTargetWordCount] = useState(1000);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [availableFiles, setAvailableFiles] = useState([]);
  const [generatedContent, setGeneratedContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Page loading effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsPageLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  // Load available files on component mount
  useEffect(() => {
    loadAvailableFiles();
  }, []);

  const loadAvailableFiles = async () => {
    try {
      const API_BASE_URL = config.apiBaseUrl;
      const response = await fetch(`${API_BASE_URL}/api/available-files`, {
        headers: getDefaultHeaders(country)
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAvailableFiles(data.files);
        }
      }
    } catch (error) {
      console.error('Error loading available files:', error);
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    setSuccess(null);

    try {
      const API_BASE_URL = config.apiBaseUrl;
      const requestBody = {
        mode,
        tone,
        targetAudience,
        additionalInstructions,
        targetWordCount
      };

      if (mode === 'manual') {
        if (selectedFiles.length === 0) {
          throw new Error('Please select at least one file for manual mode');
        }
        requestBody.selectedFiles = selectedFiles;
      }

      const response = await fetch(`${API_BASE_URL}/api/generate-board-deck`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setGeneratedContent(data.content);
          setEditedContent(data.content);
          setSuccess('Board deck generated successfully!');
        } else {
          throw new Error(data.error || 'Failed to generate board deck');
        }
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error generating board deck:', error);
      setError(error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditedContent(generatedContent);
  };

  const handleSave = () => {
    setGeneratedContent(editedContent);
    setIsEditing(false);
    setSuccess('Content updated successfully!');
  };

  const handleCancel = () => {
    setEditedContent(generatedContent);
    setIsEditing(false);
  };

  const handleFileToggle = (fileId) => {
    setSelectedFiles(prev => 
      prev.includes(fileId) 
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const downloadContent = () => {
    const blob = new Blob([generatedContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'board-deck-draft.md';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSectionClick = (sectionId) => {
    setActiveSection(sectionId);
    // Scroll to section in center panel
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const leftPanel = (
    <BoardDeckLeftPanel
      mode={mode}
      setMode={setMode}
      tone={tone}
      setTone={setTone}
      targetAudience={targetAudience}
      setTargetAudience={setTargetAudience}
      additionalInstructions={additionalInstructions}
      setAdditionalInstructions={setAdditionalInstructions}
      targetWordCount={targetWordCount}
      setTargetWordCount={setTargetWordCount}
      selectedFiles={selectedFiles}
      availableFiles={availableFiles}
      onFileToggle={handleFileToggle}
      onGenerate={handleGenerate}
      isGenerating={isGenerating}
    />
  );

  const centerPanel = (
    <BoardDeckCenterPanel
      generatedContent={generatedContent}
      editedContent={editedContent}
      setEditedContent={setEditedContent}
      isEditing={isEditing}
      setIsEditing={setIsEditing}
      onEdit={handleEdit}
      onSave={handleSave}
      onCancel={handleCancel}
      onDownload={downloadContent}
      error={error}
      success={success}
      activeSection={activeSection}
      onSectionClick={handleSectionClick}
    />
  );

  return (
    <>
      {/* Loading Overlay */}
      <LoadingOverlay
        title="AI Earnings Assistant"
        message="Initializing your presentation generator..."
        show={isPageLoading}
      />

      {/* Modern Layout with Two Panels */}
      <ModernLayout
        leftPanel={leftPanel}
        showTwoPanel={true}
      >
        {centerPanel}
      </ModernLayout>
    </>
  );
};

export default ModernBoardDeckGenerator;

